import { useState, useEffect } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Badge, ScrollArea } from '@creanizer/ui'
import { Search, Trash2, Eye, Edit, Package, Download, Folder } from 'lucide-react'
import { useItemStore } from '../../../../stores/itemStore'
import { useFolderStore } from '../../../../stores/folderStore'
import type { SavedItem, ItemInput, ItemResult } from '@creanizer/types'
import { ExportImportButtons } from '../../../../components/common'
import { ItemEditModal } from './ItemEditModal'
import { FolderManager, FolderMoveSelect } from './FolderManager'

interface SavedItemsListProps {
  onLoad: (item: { input: ItemInput; result: ItemResult }) => void
}

export function SavedItemsList({ onLoad }: SavedItemsListProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [editingItem, setEditingItem] = useState<SavedItem | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [showFolders, setShowFolders] = useState(true)
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  
  const { items, removeItem, clearItems, exportAsText, exportAsJSON, exportSingleAsText, exportSingleAsJSON, importFromJSON, fixDuplicateIds, updateItem } = useItemStore()
  const { getItemsInFolder, getItemFolder, moveItemBetweenFolders } = useFolderStore()
  
  // コンポーネントマウント時に重複IDをチェックして修正
  useEffect(() => {
    const hasDuplicates = fixDuplicateIds()
    if (hasDuplicates) {
      console.log('アイテムIDの重複を修正しました')
    }
  }, [fixDuplicateIds])

  // フォルダによるフィルタリング
  let folderFilteredItems = items
  if (selectedFolderId === 'all') {
    folderFilteredItems = items
  } else if (selectedFolderId === null) {
    // 未整理: どのフォルダにも属していないアイテム
    const allFolderItemIds = new Set<string>()
    const folders = useFolderStore.getState().getFolders('item')
    folders.forEach(folder => {
      getItemsInFolder('item', folder.id).forEach(id => allFolderItemIds.add(id))
    })
    folderFilteredItems = items.filter(item => !allFolderItemIds.has(item.id))
  } else {
    const itemIds = getItemsInFolder('item', selectedFolderId)
    folderFilteredItems = items.filter(item => itemIds.includes(item.id))
  }
  
  // 検索によるフィルタリング
  const filteredItems = folderFilteredItems.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.result.basicInfo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.result.basicInfo.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.result.basicInfo.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.tags && item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
  )

  return (
    <div className="card max-w-full overflow-x-hidden">
      <div className="flex items-center gap-2 mb-2">
        <Package className="h-5 w-5" />
        <h2 className="text-xl font-semibold">
          保存済みアイテム ({items.length}/500)
        </h2>
      </div>
      <p className="text-muted-foreground mb-6">
        生成したアイテム設定を保存・管理
      </p>
      
      <div className="space-y-4 max-w-full">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="アイテムを検索..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFolders(!showFolders)}
            className="px-2"
          >
            <Folder className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex gap-2">
          <ExportImportButtons
            onExportText={exportAsText}
            onExportJSON={exportAsJSON}
            onImportJSON={importFromJSON}
            dataTypeName="アイテム"
          />

          {items.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (confirm('すべてのアイテムを削除しますか？')) {
                  clearItems()
                }
              }}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              全削除
            </Button>
          )}
        </div>

        <div className="flex gap-4">
          {/* フォルダサイドバー */}
          {showFolders && (
            <div className="w-48 border-r pr-4">
              <FolderManager
                generatorType="item"
                items={items}
                selectedFolderId={selectedFolderId}
                onFolderSelect={setSelectedFolderId}
                draggedItemId={draggedItemId}
                onItemMove={(itemId, toFolderId) => {
                  const currentFolder = getItemFolder('item', itemId)
                  moveItemBetweenFolders('item', itemId, currentFolder?.id || null, toFolderId)
                }}
              />
            </div>
          )}
          
          {/* アイテムリスト */}
          <div className="flex-1">
            <ScrollArea className="h-[500px] w-full overflow-x-hidden">
          <div className="space-y-2 max-w-full w-full">
            {filteredItems.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {searchTerm ? '検索結果がありません' : '保存済みのアイテムがありません'}
              </p>
            ) : (
              filteredItems.map((item) => (
                <div 
                  key={item.id} 
                  className="card p-2 sm:p-4 overflow-hidden max-w-full w-[calc(100%-2rem)] sm:w-full mr-auto cursor-move hover:shadow-lg transition-shadow"
                  draggable
                  onDragStart={(e) => {
                    setDraggedItemId(item.id)
                    e.dataTransfer.effectAllowed = 'move'
                  }}
                  onDragEnd={() => {
                    setDraggedItemId(null)
                  }}
                  style={{
                    opacity: draggedItemId === item.id ? 0.5 : 1
                  }}
                >
                  {/* モバイル版: 縦並びレイアウト */}
                  <div className="flex flex-col gap-3 sm:hidden w-full">
                    <div className="w-full">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm truncate">{item.title}</h3>
                        {item.favorite && <Badge variant="secondary" className="text-xs">お気に入り</Badge>}
                      </div>
                      
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">{item.result.basicInfo.type}</Badge>
                        <Badge variant="outline" className="text-xs">{item.result.basicInfo.category}</Badge>
                        <Badge 
                          variant={
                            item.result.basicInfo.rarity === 'legendary' ? 'destructive' :
                            item.result.basicInfo.rarity === 'epic' ? 'secondary' :
                            'outline'
                          } 
                          className="text-xs"
                        >
                          {item.result.basicInfo.rarity}
                        </Badge>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-2 truncate">
                        {item.result.basicInfo.summary}
                      </p>
                      
                      {item.tags && item.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {item.tags.slice(0, 2).map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(item.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択 */}
                      <div className="mt-2">
                        <FolderMoveSelect
                          generatorType="item"
                          itemId={item.id}
                          currentFolderId={getItemFolder('item', item.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    {/* モバイル版: 横一列のボタン */}
                    <div className="flex gap-0.5 justify-start w-full">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onLoad({ input: item.input, result: item.result })}
                        title="読込"
                        className="w-12 h-7 px-1"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingItem(item)}
                        title="編集"
                        className="w-12 h-7 px-1"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsText(item.id)}
                        title="テキスト形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">txt</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsJSON(item.id)}
                        title="JSON形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">json</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('このアイテムを削除しますか？')) {
                            removeItem(item.id)
                          }
                        }}
                        className="w-12 h-7 px-1"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* PC版: 既存の横並びレイアウト */}
                  <div className="hidden sm:flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm truncate">{item.title}</h3>
                        {item.favorite && <Badge variant="secondary">お気に入り</Badge>}
                      </div>
                      
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        <span className="truncate">{item.result.basicInfo.type}</span>
                        <span>•</span>
                        <span className="truncate">{item.result.basicInfo.category}</span>
                        <span>•</span>
                        <span className={`truncate ${
                          item.result.basicInfo.rarity === 'legendary' ? 'text-destructive' :
                          item.result.basicInfo.rarity === 'epic' ? 'text-secondary' :
                          ''
                        }`}>
                          {item.result.basicInfo.rarity}
                        </span>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {item.result.basicInfo.summary}
                      </p>
                      
                      {item.tags && item.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {item.tags.slice(0, 2).map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(item.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    
                    <div className="flex gap-2">
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onLoad({ input: item.input, result: item.result })}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          読込
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsText(item.id)}
                          title="テキスト形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          txt
                        </Button>
                      </div>
                      
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingItem(item)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          編集
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsJSON(item.id)}
                          title="JSON形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          json
                        </Button>
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('このアイテムを削除しますか？')) {
                            removeItem(item.id)
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
            </ScrollArea>
          </div>
        </div>

        {items.length >= 450 && (
          <p className="text-xs text-muted-foreground text-center">
            保存可能件数の上限（500件）に近づいています
          </p>
        )}
      </div>
      
      {editingItem && (
        <ItemEditModal
          isOpen={!!editingItem}
          onClose={() => setEditingItem(null)}
          onSave={(updatedResult) => {
            updateItem(editingItem.id, {
              result: { ...editingItem.result, ...updatedResult },
              updatedAt: new Date().toISOString()
            })
            setEditingItem(null)
          }}
          itemResult={editingItem.result}
          title={`「${editingItem.title}」を編集`}
        />
      )}
    </div>
  )
}